import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Layers } from "lucide-react";
import { generateSocialReport, useAction } from "wasp/client/operations";
import { Button } from "../../../client/components/ui/button";
import { useLanguageContext } from "../../../client/contexts/LanguageContext";
import useTranslation from "../../../client/hooks/useTranslation";
import { trackSocialReportGenerated } from "../../../client/utils/posthog";
import type { ReportScope, ReviewPeriod } from "../../../social-reports/metrics/types";
import { useLiteData } from "../../data/LiteDataProvider";
import { LiteDrawer } from "../../ui/LiteDrawer";
import { Select, type Option } from "../../ui/Select";
import { Segmented, labelCls } from "../../ui/ui";
import { isCurrentOrFuture, periodLabel, referenceIso, shiftPeriod } from "./reportPeriod";

const PERIODS: ReviewPeriod[] = ["monthly", "quarterly", "annual"];

/** Périmètre encodé pour le Select : `null` = comptes actifs. */
type ScopeKey = "all-inactive" | `propfirm:${string}` | `account:${string}`;

function toScope(key: ScopeKey | null): ReportScope {
  if (key === null) return { type: "all" };
  if (key === "all-inactive") return { type: "all-inactive" };
  const [type, id] = key.split(":") as ["propfirm" | "account", string];
  return { type, id };
}

/** Tiroir de génération d'un bilan : période, navigation, périmètre. Ouvre le détail une fois calculé. */
export function GenerateReportDrawer({ open, onClose, onGenerated }: { open: boolean; onClose: () => void; onGenerated: () => void }) {
  const tl = useTranslation().lite;
  const t = tl.reports;
  const { language } = useLanguageContext();
  const { firms, accounts } = useLiteData();
  const navigate = useNavigate();
  const generate = useAction(generateSocialReport);

  const [period, setPeriod] = useState<ReviewPeriod>("monthly");
  const [ref, setRef] = useState(() => new Date());
  const [scopeKey, setScopeKey] = useState<ScopeKey | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const presets: Option<ScopeKey>[] = [{ value: "all-inactive", label: t.drawer.scopeAllInactive }];
  const options: Option<ScopeKey>[] = [
    ...firms.map(f => ({ value: `propfirm:${f.id}` as const, label: f.name, group: t.drawer.groupFirms })),
    ...accounts.map(a => ({ value: `account:${a.id}` as const, label: a.name, hint: a.firm?.name, group: t.drawer.groupAccounts })),
  ];

  const changePeriod = (p: ReviewPeriod) => { setPeriod(p); setRef(new Date()); };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await generate({ period, scope: toScope(scopeKey), referenceDate: referenceIso(period, ref), skipRender: true });
      trackSocialReportGenerated("lite");
      onGenerated();
      onClose();
      navigate(`/reports/${r.reportId}`);
    } catch {
      setError(t.drawer.error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <LiteDrawer
      open={open}
      title={t.drawer.title}
      subtitle={t.drawer.subtitle}
      onClose={onClose}
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={busy}>{tl.common.cancel}</Button>
        <Button onClick={submit} disabled={busy}>{busy ? t.drawer.generating : t.generate}</Button>
      </>}
    >
      <div>
        <span className={labelCls}>{t.drawer.period}</span>
        <Segmented value={period} onChange={changePeriod} options={PERIODS.map(p => ({ id: p, label: t.periods[p] }))} />
        <div className="flex items-center justify-between gap-2 mt-3 rounded-xl border border-border bg-card px-1 h-11">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setRef(d => shiftPeriod(period, d, -1))} aria-label={t.drawer.previous}><ChevronLeft size={16} /></Button>
          <span className="font-display font-semibold">{periodLabel(period, ref, language)}</span>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setRef(d => shiftPeriod(period, d, 1))} disabled={isCurrentOrFuture(period, ref)} aria-label={t.drawer.next}><ChevronRight size={16} /></Button>
        </div>
      </div>
      <div>
        <span className={labelCls}>{t.drawer.scope}</span>
        <Select<ScopeKey>
          label={t.drawer.scope}
          value={scopeKey}
          presets={presets}
          options={options}
          allLabel={t.drawer.scopeActive}
          onChange={setScopeKey}
          icon={<Layers size={14} />}
          quiet
        />
      </div>
      {error && <p className="text-sm text-pnlNegative">{error}</p>}
    </LiteDrawer>
  );
}
